import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Eye, Edit, Trash2, Users, Briefcase, Settings, MoreVertical, Calendar, ChevronLeft, ChevronRight, UserPlus, Building2, X, Check, Upload, Search, ArrowUpDown, ArrowUp, ArrowDown, User, Pin } from 'lucide-react';
import { toast } from 'react-hot-toast';

import { apiClient } from '../../lib/api/client';
import { Requirement } from '../../lib/types/api';
import { Button } from '../../components/ui/Button';
import { useProcessingNotifications } from '../../hooks/useProcessingNotifications';
import { ConfirmationDialog } from '../../components/ui/ConfirmationDialog';
// Removed RequirementForm import - now using dedicated pages
import { BulkUploadRequirementsModal } from './BulkUploadRequirementsModal';
import { BulkUploadResumesModal } from '../candidates/BulkUploadResumesModal';
import { EdgeActions } from '../../components/ui/EdgeActions';
import { FilterDrawer } from '../../components/ui/FilterDrawer';
import { ClientFilterDropdown } from '../../components/ui/ClientFilterDropdown';
import { PortalDropdown } from '../../components/ui/PortalDropdown';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

export default function Requirements() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  // Removed showRequirementForm state - now using navigation
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  // Removed editingRequirement state - now using dedicated edit page
  const [deletingRequirement, setDeletingRequirement] = useState<Requirement | null>(null);
  const [editingRequirementId, setEditingRequirementId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [openMenuForId, setOpenMenuForId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [showBulkUploadCandidates, setShowBulkUploadCandidates] = useState(false);
  const [selectedRequirementForBulkUpload, setSelectedRequirementForBulkUpload] = useState<Requirement | null>(null);
  
  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Client-side search state
  const [allRequirements, setAllRequirements] = useState<Requirement[]>([]);
  const [filteredRequirements, setFilteredRequirements] = useState<Requirement[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sortButtonRef = useRef<HTMLButtonElement>(null);
  
  // Filter state
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    seniority: '',
    client_id: '',
    category: '',
    experience_min: '',
    experience_max: ''
  });

  const queryClient = useQueryClient();
  
  // Enable processing notifications
  useProcessingNotifications();

  // Client-side search function
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1); // Reset to first page when searching
    setIsSearching(true);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      setIsSearching(false);
    }, 150); // Reduced timeout for better UX
    
    setSearchTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSortOptions) {
        const target = event.target as Element;
        if (!target.closest('.sort-dropdown')) {
          setShowSortOptions(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSortOptions]);

  // Filter functions
  const handleApplyFilters = () => {
    setPage(1); // Reset to first page when applying filters
    setShowFilterDrawer(false);
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      seniority: '',
      client_id: '',
      category: '',
      experience_min: '',
      experience_max: ''
    });
    setPage(1);
  };

  // Fetch requirements - use server-side search when search query exists for full coverage
  const { data: requirements, isLoading, error } = useQuery({
    queryKey: ['requirements-all', filters, searchQuery], // Include searchQuery for cache invalidation
    queryFn: () => {
      const params = {
        page: 1,
        page_size: 300, // Fetch all at once
        // Send search to backend when provided - this enables searching across ALL requirements
        // (not just the first 300) which is important when there are 1000+ requirements
        search: searchQuery?.trim() || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        status: filters.status || undefined,
        client_id: filters.client_id || undefined,
        category: filters.category || undefined,
        seniority: filters.seniority || undefined,
        experience_min: filters.experience_min ? parseInt(filters.experience_min) : undefined,
        experience_max: filters.experience_max ? parseInt(filters.experience_max) : undefined,
      };
      console.log('🔍 Fetching requirements with params:', params);
      // Add cache-busting parameter to force fresh data
      const paramsWithCacheBust = { ...params, _t: Date.now() };
      return apiClient.getRequirementsList(paramsWithCacheBust);
    },
    staleTime: 0, // No cache - always fetch fresh data
    placeholderData: undefined
  });

  // Store all requirements for client-side search
  useEffect(() => {
    if (requirements?.requirements) {
      setAllRequirements(requirements.requirements);
    }
  }, [requirements]);

  // Generate search suggestions
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const suggestions = allRequirements
      .map(req => req.title)
      .filter(title => title.toLowerCase().includes(query))
      .slice(0, 5)
      .filter((title, index, self) => self.indexOf(title) === index); // Remove duplicates

    setSearchSuggestions(suggestions);
    setShowSuggestions(suggestions.length > 0);
  }, [searchQuery, allRequirements]);

  // Client-side search, filtering, and sorting
  useEffect(() => {
    if (!allRequirements.length) {
      setFilteredRequirements([]);
      return;
    }

    console.log('🔍 [FRONTEND] Processing requirements:', allRequirements.length);
    console.log('🔍 [FRONTEND] First requirement pin status:', allRequirements[0]?.is_pinned, allRequirements[0]?.id);

    let filtered = [...allRequirements];

    // Client-side text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(req => 
        req.title.toLowerCase().includes(query) ||
        req.description?.toLowerCase().includes(query) ||
        req.client_name?.toLowerCase().includes(query) ||
        (req as any).created_by_name?.toLowerCase().includes(query)
      );
    }

    // Client-side sorting with pin priority
    filtered.sort((a, b) => {
      // First, separate pinned and unpinned
      const aPinned = a.is_pinned || false;
      const bPinned = b.is_pinned || false;
      
      console.log(`Sorting: ${a.title} (pinned: ${aPinned}) vs ${b.title} (pinned: ${bPinned})`);
      
      // Pinned items always come first
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      
      // If both have same pin status, apply normal sorting
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'client_name':
          aValue = a.client_name?.toLowerCase() || '';
          bValue = b.client_name?.toLowerCase() || '';
          break;
        case 'status':
          aValue = a.status?.toLowerCase() || '';
          bValue = b.status?.toLowerCase() || '';
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at || 0).getTime();
          bValue = new Date(b.updated_at || 0).getTime();
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredRequirements(filtered);
  }, [allRequirements, searchQuery, sortBy, sortOrder]);

  // Use filtered requirements for display with pagination
  const totalCount = filteredRequirements.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  
  // Get paginated results from filtered requirements
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const requirementsList = filteredRequirements.slice(startIndex, endIndex);

  // Candidate counts are now provided by the optimized API response

  const getCandidatesCount = (requirement: any) => {
    // Use candidates_count from the optimized API response
    return requirement.candidates_count || 0;
  };

  // Search highlighting utility
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">$1</mark>');
  };


  // Delete requirement mutation
  const deleteRequirementMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteRequirement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements-all'] });
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      setDeletingRequirement(null);
      toast.success('Requirement deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete requirement');
    }
  });


  const handleDeleteRequirement = (requirement: Requirement) => {
    setDeletingRequirement(requirement);
  };

  const confirmDelete = () => {
    if (deletingRequirement) {
      deleteRequirementMutation.mutate(deletingRequirement.id);
    }
  };

  const handleEditRequirement = async (requirement: Requirement) => {
    setEditingRequirementId(requirement.id);
    try {
      // Navigate to edit page instead of opening modal
      navigate(`/requirements/${requirement.id}/edit`);
    } catch (error) {
      console.error('Failed to navigate to edit page:', error);
      toast.error('Failed to open edit page');
    } finally {
      setEditingRequirementId(null);
    }
  };

  const handleAddCandidate = (requirement: Requirement) => {
    navigate(`/candidates/submit/${requirement.id}`);
  };

  const handleViewCandidates = (requirement: Requirement) => {
    navigate(`/requirements/${requirement.id}/candidates`);
  };

  const handleBulkUploadCandidates = (requirement: Requirement) => {
    setSelectedRequirementForBulkUpload(requirement);
    setShowBulkUploadCandidates(true);
  };

  const handleViewRequirement = (requirement: Requirement) => {
    navigate(`/requirements/${requirement.id}/view`, { 
      state: { from: location.pathname } 
    });
  };

  const handlePinToggle = async (e: React.MouseEvent, requirement: Requirement) => {
    e.stopPropagation();
    console.log('Pin toggle clicked for requirement:', requirement.id, 'Current pin status:', requirement.is_pinned);
    try {
      if (requirement.is_pinned) {
        await apiClient.unpinRequirement(requirement.id);
        toast.success('Requirement unpinned');
      } else {
        await apiClient.pinRequirement(requirement.id);
        toast.success('Requirement pinned');
      }
      queryClient.invalidateQueries({ queryKey: ['requirements-all'] });
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error('Failed to update pin status');
    }
  };

  const handleMenuClick = (e: React.MouseEvent, requirementId: string) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX
    });
    setOpenMenuForId(requirementId);
  };

  const closeMenu = () => {
    setOpenMenuForId(null);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuForId) {
        closeMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuForId]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Error loading requirements</div>
          <div className="text-text-tertiary dark:text-text-tertiary">{error.message}</div>
          </div>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-enterprise-primary via-background-enterprise-secondary to-background-enterprise-primary relative z-0">
      {/* EdgeActions */}
      <EdgeActions
        onUpload={() => setShowBulkUpload(true)}
        onAdd={() => navigate('/requirements/create')}
        onFilter={() => setShowFilterDrawer(true)}
        showUpload={hasPermission('requirements:write')}
        showAdd={hasPermission('requirements:write')}
        showFilter={true}
      />

      {/* Search and Sort Bar */}
      <div className="px-6 py-4 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border-b border-glass-border/50 dark:border-glass-border-dark-mode/50 relative overflow-visible z-50">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          {/* Search and Client Filter */}
          <div className="flex flex-col sm:flex-row gap-3 flex-1 max-w-2xl">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by title or client..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setShowSuggestions(searchSuggestions.length > 0)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="w-full pl-10 pr-10 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 backdrop-blur-xl border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg text-text-enterprise-primary placeholder-text-enterprise-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary hover:text-text-enterprise-primary transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              
              {/* Search Suggestions Dropdown - Portal */}
              <PortalDropdown
                isOpen={showSuggestions && searchSuggestions.length > 0}
                triggerRef={searchInputRef}
                onClose={() => setShowSuggestions(false)}
                className="max-h-60"
              >
                <div className="py-1">
                  {searchSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        handleSearchChange(suggestion);
                        setShowSuggestions(false);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 transition-colors text-text-enterprise-primary"
                    >
                      <div 
                        className="truncate"
                        dangerouslySetInnerHTML={{
                          __html: highlightSearchTerm(suggestion, searchQuery)
                        }}
                      />
                    </button>
                  ))}
                </div>
              </PortalDropdown>
              
              {/* Search Results Count */}
              {searchQuery && (
                <div className="absolute -bottom-6 left-0 text-xs text-text-enterprise-secondary">
                  {isSearching ? (
                    <span className="flex items-center gap-1">
                      <LoadingSpinner size="xs" variant="ai" />
                      Searching...
                    </span>
                  ) : (
                    <span>
                      Found {totalCount} of {allRequirements.length} requirements
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Compact Client Filter */}
            <div className="w-full sm:w-48">
              <ClientFilterDropdown
                value={filters.client_id}
                onChange={(clientId) => setFilters(prev => ({ ...prev, client_id: clientId }))}
                placeholder="All Clients"
                compact={true}
              />
            </div>
          </div>

          {/* Sort Options */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-enterprise-secondary">Sort by:</span>
            <div className="relative">
              <button
                ref={sortButtonRef}
                onClick={() => setShowSortOptions(!showSortOptions)}
                className="sort-dropdown flex items-center gap-2 px-3 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 backdrop-blur-xl border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 transition-all duration-200"
              >
                <ArrowUpDown className="w-4 h-4" />
                <span className="text-sm capitalize">
                  {sortBy === 'created_at' ? 'Date Created' : 
                   sortBy === 'title' ? 'Title' :
                   sortBy === 'client_name' ? 'Client' :
                   sortBy === 'status' ? 'Status' :
                   sortBy === 'updated_at' ? 'Last Updated' : sortBy}
                </span>
                <div className="flex items-center gap-1">
                  {sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  <ChevronRight className="w-3 h-3 rotate-90" />
                </div>
              </button>

              {/* Sort Dropdown - Portal */}
              <PortalDropdown
                isOpen={showSortOptions}
                triggerRef={sortButtonRef}
                onClose={() => setShowSortOptions(false)}
                className="w-56 min-w-max"
              >
                <div className="py-2">
                  {[
                    { value: 'created_at', label: 'Date Created' },
                    { value: 'title', label: 'Title' },
                    { value: 'client_name', label: 'Client' },
                    { value: 'status', label: 'Status' },
                    { value: 'updated_at', label: 'Last Updated' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortOptions(false);
                        setPage(1);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 transition-colors flex items-center justify-between ${
                        sortBy === option.value ? 'text-primary-600 dark:text-primary-400 bg-primary-500/10' : 'text-text-enterprise-primary'
                      }`}
                    >
                      {option.label}
                      {sortBy === option.value && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
                <div className="border-t border-glass-border/50 dark:border-glass-border-dark-mode/50 py-1">
                  <button
                    onClick={() => {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      setPage(1);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-text-enterprise-primary hover:bg-glass-surface/70 dark:hover:bg-glass-surface-dark-mode/70 transition-colors flex items-center gap-2"
                  >
                    {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                    {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  </button>
                </div>
              </PortalDropdown>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 lg:p-8 xl:p-10 relative z-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" variant="ai" />
          </div>
        ) : requirementsList.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-text-enterprise-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-enterprise-primary mb-2">No requirements found</h3>
            <p className="text-text-enterprise-secondary mb-6">
              {hasPermission('requirements:write') ? 'Get started by creating your first requirement' : 'No requirements available to view'}
            </p>
            {hasPermission('requirements:write') && (
              <Button onClick={() => navigate('/requirements/create')}>
                <Plus className="w-4 h-4 mr-2" />
                Add Requirement
              </Button>
            )}
                    </div>
        ) : (
          <>
            {/* Desktop Table View */}
            {!isLoading && (
              <div className="hidden lg:block">
                <div className="rounded-xl border border-glass-border/40 dark:border-glass-border-dark-mode/40 shadow-sm bg-gradient-to-r from-glass-surface/80 via-glass-surface/90 to-glass-surface/80 dark:from-glass-surface-dark-mode/80 dark:via-glass-surface-dark-mode/90 dark:to-glass-surface-dark-mode/80 backdrop-blur-xl relative z-0 overflow-x-auto">
                  <table className="w-full table-auto" style={{ tableLayout: 'auto' }}>
              <colgroup>
                      <col style={{ minWidth: '200px' }} />
                      <col style={{ minWidth: '120px' }} />
                      <col style={{ minWidth: '100px' }} />
                      <col style={{ minWidth: '150px' }} />
                      <col style={{ minWidth: '120px' }} />
              </colgroup>
                <thead className="sticky top-0 z-10 bg-gradient-to-r from-glass-primary/40 via-glass-secondary/35 to-glass-primary/40 border-b border-glass-border/50 backdrop-blur-xl">
                  <tr>
                        <th className="px-6 py-4 text-left ds-table-header">
                      <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                            <span className="font-semibold tracking-wide text-text-enterprise-primary">Job Title</span>
                      </div>
                    </th>
                        <th className="px-6 py-4 text-left ds-table-header">
                      <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            <span className="font-semibold tracking-wide text-text-enterprise-primary">Client</span>
                      </div>
                    </th>
                        <th className="px-6 py-4 text-left ds-table-header">
                      <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            <span className="font-semibold tracking-wide text-text-enterprise-primary">Candidates</span>
                      </div>
                    </th>
                        <th className="px-6 py-4 text-left ds-table-header">
                      <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                            <span className="font-semibold tracking-wide text-text-enterprise-primary">Created By & At</span>
                      </div>
                    </th>
                        <th className="px-6 py-4 text-right ds-table-header">
                      <div className="flex items-center gap-2 justify-end">
                            <Settings className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            <span className="font-semibold tracking-wide text-text-enterprise-primary">Actions</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                    <tbody className="divide-y divide-glass-border/30">
                  {requirementsList.map((requirement: any) => {
                    const candidatesCount = getCandidatesCount(requirement);
                    
                    return (
                          <tr key={requirement.id} className={`hover:bg-glass-surface/30 transition-colors duration-200 ${requirement.is_pinned ? 'bg-yellow-500/10 dark:bg-yellow-500/20 border-l-2 border-yellow-400/30 dark:border-yellow-400/40' : ''}`}>
                            <td className="px-6 py-4 max-w-[250px] md:max-w-[300px] lg:max-w-[450px] xl:max-w-[550px]">
                              <div className="flex items-start gap-2">
                                <button
                                  onClick={(e) => handlePinToggle(e, requirement)}
                                  className={`p-1.5 rounded-lg transition-all duration-200 hover:scale-110 shrink-0 ${
                                    requirement.is_pinned 
                                      ? 'text-yellow-500 hover:bg-yellow-500/20 bg-yellow-500/10' 
                                      : 'text-text-enterprise-tertiary hover:bg-glass-surface/50 hover:text-primary-600 dark:hover:text-primary-400'
                                  }`}
                                  title={requirement.is_pinned ? 'Unpin requirement' : 'Pin requirement'}
                                >
                                  <Pin className={`w-4 h-4 transition-all duration-200 ${requirement.is_pinned ? 'fill-current rotate-12' : 'hover:rotate-12'}`} />
                                </button>
                                <div className="flex-1 min-w-0">
                                  <button
                                    onClick={() => handleViewRequirement(requirement)}
                                    className="font-bold text-base lg:text-lg text-text-enterprise-primary hover:text-primary-300 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed block w-full"
                                    title={requirement.title}
                                  >
                                    <div 
                                      className="truncate" 
                                      dangerouslySetInnerHTML={{
                                        __html: searchQuery ? highlightSearchTerm(requirement.title, searchQuery) : requirement.title
                                      }}
                                    />
                                  </button>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-text-enterprise-secondary">
                                    <div className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      <span>{requirement.positions || 1} Position{(requirement.positions || 1) > 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Briefcase className="w-3 h-3" />
                                      <span className="capitalize">{requirement.employment_type || 'Not specified'}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                          </td>
                          
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                              <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                <span className="font-semibold text-text-enterprise-primary truncate">
                                {requirement.client_name || 'Default Client'}
                              </span>
                            </div>
                          </td>
                            
                            <td className="px-6 py-4">
                              <button onClick={() => navigate(`/requirements/${requirement.id}/candidates`)} className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full border border-blue-500/30 hover:bg-blue-500/30 backdrop-blur-sm transition-all duration-200">
                                <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              <span className="font-bold text-text-enterprise-primary text-sm">{candidatesCount}</span>
                            </button>
                          </td>
                          
                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                  <span className="text-sm font-medium text-text-enterprise-primary truncate">
                                    {requirement.created_by_name || 'Unknown'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  <span className="text-xs text-text-enterprise-secondary">
                                    {requirement.created_at ? new Date(requirement.created_at).toLocaleDateString() : 'Unknown'}
                                  </span>
                                </div>
                              </div>
                          </td>
                          
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-0.5 flex-wrap justify-end max-w-[200px] lg:max-w-[250px] xl:max-w-[300px]">
                              <button
                                  onClick={() => handleViewRequirement(requirement)}
                                  className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="View Requirement"
                                >
                                    <Eye className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-300" />
                              </button>
                                <button
                                  onClick={() => handleAddCandidate(requirement)}
                                  className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group"
                                  title="Add Candidate & Upload CV"
                                >
                                  <UserPlus className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-300" />
                                </button>
                                <button
                                  onClick={() => handleBulkUploadCandidates(requirement)}
                                  className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group"
                                  title="Bulk Upload Candidates"
                                >
                                  <Upload className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-300" />
                                </button>
                                {hasPermission('requirements:write') && (
                                  <>
                                      <button
                                      onClick={() => handleEditRequirement(requirement)}
                                      className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Edit Requirement"
                                      disabled={editingRequirementId === requirement.id}
                                      >
                                      {editingRequirementId === requirement.id ? (
                                        <LoadingSpinner size="sm" variant="ai" className="text-primary-600 dark:text-primary-400" />
                                      ) : (
                                        <Edit className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-300" />
                                      )}
                                      </button>
                                      <button
                                      onClick={() => handleDeleteRequirement(requirement)}
                                      className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group"
                                      title="Delete Requirement"
                                      >
                                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400 group-hover:text-red-500 dark:group-hover:text-red-300" />
                                      </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

            {/* Tablet Table View */}
            {!isLoading && (
              <div className="hidden md:block lg:hidden">
                <div className="rounded-xl border border-glass-border/40 dark:border-glass-border-dark-mode/40 shadow-sm bg-gradient-to-r from-glass-surface/80 via-glass-surface/90 to-glass-surface/80 dark:from-glass-surface-dark-mode/80 dark:via-glass-surface-dark-mode/90 dark:to-glass-surface-dark-mode/80 backdrop-blur-xl relative z-0 overflow-x-auto">
                  <table className="w-full table-auto" style={{ tableLayout: 'auto' }}>
              <colgroup>
                      <col style={{ minWidth: '200px' }} />
                      <col style={{ minWidth: '120px' }} />
                      <col style={{ minWidth: '100px' }} />
                      <col style={{ minWidth: '150px' }} />
              </colgroup>
                <thead className="sticky top-0 z-10 bg-gradient-to-r from-glass-primary/40 via-glass-secondary/35 to-glass-primary/40 border-b border-glass-border/50 backdrop-blur-xl">
                  <tr>
                        <th className="px-4 py-3 text-left ds-table-header">
                      <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                            <span className="font-semibold tracking-wide">Job Title</span>
                      </div>
                    </th>
                        <th className="px-4 py-3 text-left ds-table-header">
                      <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            <span className="font-semibold tracking-wide text-text-enterprise-primary">Client</span>
                      </div>
                    </th>
                        <th className="px-4 py-3 text-left ds-table-header">
                      <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            <span className="font-semibold tracking-wide text-text-enterprise-primary">Candidates</span>
                      </div>
                    </th>
                        <th className="px-4 py-3 text-right ds-table-header">
                      <div className="flex items-center gap-2 justify-end">
                            <Settings className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            <span className="font-semibold tracking-wide text-text-enterprise-primary">Actions</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                    <tbody className="divide-y divide-glass-border/30">
                  {requirementsList.map((requirement: any) => {
                    const candidatesCount = getCandidatesCount(requirement);
                    
                    return (
                          <tr key={requirement.id} className="hover:bg-glass-surface/30 transition-colors duration-200">
                            <td className="px-4 py-3 max-w-[200px] md:max-w-[280px] lg:max-w-[350px]">
                              <div className="flex-1 min-w-0">
                                <button
                                  onClick={() => handleViewRequirement(requirement)}
                                  className="font-bold text-base text-text-enterprise-primary hover:text-primary-300 transition-colors truncate text-left disabled:opacity-50 disabled:cursor-not-allowed block w-full"
                                  title={requirement.title}
                                >
                                  {requirement.title}
                                </button>
                                <div className="text-sm text-text-enterprise-secondary truncate">
                                {requirement.seniority && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 dark:bg-blue-500/30 text-blue-700 dark:text-blue-400 border border-blue-500/30 dark:border-blue-500/40 mr-2">
                                      {requirement.seniority}
                                    </span>
                                  )}
                                  {requirement.experience?.min_years && requirement.experience?.max_years && (
                                    <span className="text-xs text-text-enterprise-tertiary">
                                      {requirement.experience.min_years}-{requirement.experience.max_years} years exp
                                    </span>
                                )}
                              </div>
                            </div>
                          </td>
                          
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                <span className="text-sm font-medium text-text-enterprise-primary truncate">
                                {requirement.client_name || 'Default Client'}
                              </span>
                            </div>
                          </td>
                          
                            <td className="px-4 py-3">
                              <button onClick={() => navigate(`/requirements/${requirement.id}/candidates`)} className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full border border-blue-500/30 hover:bg-blue-500/30 backdrop-blur-sm transition-all duration-200">
                                <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              <span className="font-bold text-text-enterprise-primary text-sm">{candidatesCount}</span>
                            </button>
                          </td>
                          
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-0.5 flex-wrap justify-end max-w-[180px] lg:max-w-[220px] xl:max-w-[260px]">
                              <button
                                  onClick={() => handleViewRequirement(requirement)}
                                  className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="View Requirement"
                                >
                                    <Eye className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-300" />
                              </button>
                                <button
                                  onClick={() => handleAddCandidate(requirement)}
                                  className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group"
                                  title="Add Candidate & Upload CV"
                                >
                                  <UserPlus className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-300" />
                                </button>
                                <button
                                  onClick={() => handleBulkUploadCandidates(requirement)}
                                  className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group"
                                  title="Bulk Upload Candidates"
                                >
                                  <Upload className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-300" />
                                </button>
                                {hasPermission('requirements:write') && (
                                  <>
                                    <button
                                      onClick={() => handleEditRequirement(requirement)}
                                      className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Edit Requirement"
                                      disabled={editingRequirementId === requirement.id}
                                    >
                                      {editingRequirementId === requirement.id ? (
                                        <LoadingSpinner size="sm" variant="ai" className="text-primary-600 dark:text-primary-400" />
                                      ) : (
                                        <Edit className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-300" />
                                      )}
                                    </button>
                                    <button
                                      onClick={() => handleDeleteRequirement(requirement)}
                                      className="p-1.5 hover:bg-glass-surface/50 rounded-lg transition-colors group"
                                      title="Delete Requirement"
                                    >
                                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400 group-hover:text-red-500 dark:group-hover:text-red-300" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

            {/* Mobile Card View */}
            {!isLoading && (
              <div className="block md:hidden p-6 space-y-6">
            {requirementsList.map((requirement: any) => {
              const candidatesCount = getCandidatesCount(requirement);
              return (
                    <div key={requirement.id} className={`bg-gradient-to-br from-glass-surface/95 to-glass-surface/90 backdrop-blur-xl rounded-2xl shadow-lg border border-glass-border/50 p-6 hover:shadow-xl transition-all duration-500 hover:scale-105 hover:-translate-y-1 animate-fade-in-up relative z-10 ${requirement.is_pinned ? 'border-l-4 border-l-yellow-400' : ''}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <button
                          onClick={(e) => handlePinToggle(e, requirement)}
                          className={`p-1.5 rounded-lg transition-all duration-200 hover:scale-110 ${
                            requirement.is_pinned 
                              ? 'text-yellow-500 hover:bg-yellow-500/20 bg-yellow-500/10' 
                              : 'text-text-enterprise-tertiary hover:bg-glass-surface/50 hover:text-primary-400'
                          }`}
                          title={requirement.is_pinned ? 'Unpin requirement' : 'Pin requirement'}
                        >
                          <Pin className={`w-4 h-4 transition-all duration-200 ${requirement.is_pinned ? 'fill-current rotate-12' : 'hover:rotate-12'}`} />
                        </button>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-lg text-text-enterprise-primary truncate">
                            {requirement.title}
                          </h3>
                          <p className="text-sm text-text-enterprise-secondary mt-1 flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {requirement.client_name || 'Default Client'}
                          </p>
                        </div>
                      </div>
                    </div>
                        <button
                          onClick={(e) => handleMenuClick(e, requirement.id)}
                          className="p-2 hover:bg-glass-surface/50 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-text-enterprise-tertiary" />
                    </button>
                  </div>
                  
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => handleViewCandidates(requirement)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full border border-blue-500/30 hover:bg-blue-500/30 backdrop-blur-sm transition-all duration-200"
                          >
                                <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            <span className="font-bold text-text-enterprise-primary text-sm">{candidatesCount} Candidates</span>
                          </button>
                          <button
                            onClick={() => handleAddCandidate(requirement)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-emerald-500/20 to-green-500/20 rounded-full border border-emerald-500/30 hover:bg-emerald-500/30 backdrop-blur-sm transition-all duration-200"
                            title="Add Candidate & Upload CV"
                          >
                            <UserPlus className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            <span className="font-bold text-text-enterprise-primary text-sm">Add Candidate</span>
                          </button>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-text-enterprise-primary">Active</span>
                          </div>
                        </div>
                      </div>
                </div>
              );
            })}
          </div>
        )}


            {/* Pagination */}
            {totalCount > 0 && (
              <div className="mt-4 bg-white dark:bg-surface-dark-100 rounded-xl p-4 border border-border-100 dark:border-border-dark-100 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-text-secondary dark:text-text-secondary">
                    Showing <span className="font-semibold">{((page - 1) * pageSize) + 1}</span> to <span className="font-semibold">{Math.min(page * pageSize, totalCount)}</span> of <span className="font-semibold">{totalCount}</span> requirements
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-3">
                      <Button variant="outline" onClick={() => setPage(prev => Math.max(prev - 1, 1))} disabled={page === 1} className="px-3 py-2">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <Button
                              key={pageNum}
                              variant={page === pageNum ? 'default' : 'outline'}
                              onClick={() => setPage(pageNum)}
                              className="w-8 h-8 p-0"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button variant="outline" onClick={() => setPage(prev => Math.min(prev + 1, totalPages))} disabled={page === totalPages} className="px-3 py-2">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        </div>

      {/* Modals - Create/Edit now handled by dedicated pages */}

      {hasPermission('requirements:write') && showBulkUpload && (
        <BulkUploadRequirementsModal
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
        />
      )}

      {deletingRequirement && (
        <ConfirmationDialog
          isOpen={!!deletingRequirement}
          onClose={() => setDeletingRequirement(null)}
          options={{
            title: 'Delete Requirement',
            description: `Are you sure you want to delete "${deletingRequirement.title}"? This action cannot be undone and will permanently remove the requirement from the system.`,
            confirmText: 'Delete Requirement',
            variant: 'destructive',
            onConfirm: confirmDelete
          }}
        />
      )}

      {/* View Requirement Modal - Removed, now using dedicated page */}
      {openMenuForId && (
        <div
          className="fixed z-[150] bg-glass-surface/95 backdrop-blur-xl border border-glass-border/50 rounded-lg shadow-xl py-2 min-w-[160px]"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
        >
          <button
            onClick={() => {
              const requirement = requirementsList.find((r: any) => r.id === openMenuForId);
              if (requirement) {
                handleViewRequirement(requirement);
              }
              closeMenu();
            }}
            className="w-full px-4 py-2 text-left text-sm text-text-enterprise-primary hover:bg-glass-surface/50 transition-colors flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            View Requirement
          </button>
          <button
            onClick={() => {
              const requirement = requirementsList.find((r: any) => r.id === openMenuForId);
              if (requirement) {
                handleViewCandidates(requirement);
              }
              closeMenu();
            }}
            className="w-full px-4 py-2 text-left text-sm text-text-enterprise-primary hover:bg-glass-surface/50 transition-colors flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            View Candidates
          </button>
          <button
            onClick={() => {
              const requirement = requirementsList.find((r: any) => r.id === openMenuForId);
              if (requirement) {
                handleAddCandidate(requirement);
              }
              closeMenu();
            }}
            className="w-full px-4 py-2 text-left text-sm text-text-enterprise-primary hover:bg-glass-surface/50 transition-colors flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Add Candidate & Upload CV
          </button>
          <button
            onClick={() => {
              const requirement = requirementsList.find((r: any) => r.id === openMenuForId);
              if (requirement) {
                handleBulkUploadCandidates(requirement);
              }
              closeMenu();
            }}
            className="w-full px-4 py-2 text-left text-sm text-text-enterprise-primary hover:bg-glass-surface/50 transition-colors flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Bulk Upload Candidates
          </button>
          {hasPermission('requirements:write') && (
            <>
              <button
                onClick={() => {
                  const requirement = requirementsList.find((r: any) => r.id === openMenuForId);
                  if (requirement) {
                    handleEditRequirement(requirement);
                  }
                  closeMenu();
                }}
                className="w-full px-4 py-2 text-left text-sm text-text-enterprise-primary hover:bg-glass-surface/50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={editingRequirementId === openMenuForId}
              >
                {editingRequirementId === openMenuForId ? (
                  <LoadingSpinner size="sm" variant="ai" />
                ) : (
                  <Edit className="w-4 h-4" />
                )}
                Edit Requirement
              </button>
              <button
                onClick={() => {
                  const requirement = requirementsList.find((r: any) => r.id === openMenuForId);
                  if (requirement) {
                    handleDeleteRequirement(requirement);
                  }
                  closeMenu();
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Requirement
              </button>
            </>
          )}
        </div>
      )}

      {/* Bulk Upload Candidates Modal */}
      {showBulkUploadCandidates && selectedRequirementForBulkUpload && (
        <BulkUploadResumesModal
          requirementId={selectedRequirementForBulkUpload.id}
          isOpen={showBulkUploadCandidates}
          onClose={() => {
            setShowBulkUploadCandidates(false);
            setSelectedRequirementForBulkUpload(null);
          }}
          onInitiated={(_, count) => {
            toast.success(`Bulk upload initiated for ${count} candidates`);
            queryClient.invalidateQueries({ queryKey: ['requirements-all'] });
            queryClient.invalidateQueries({ queryKey: ['requirements'] });
          }}
        />
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        title="Filter Requirements"
      >
        <div className="space-y-6">
          {/* Client Filter */}
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-secondary mb-2">
              Client
            </label>
            <ClientFilterDropdown
              value={filters.client_id}
              onChange={(clientId) => setFilters(prev => ({ ...prev, client_id: clientId }))}
              placeholder="All Clients"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-secondary mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full p-2 border border-border-100 dark:border-border-dark-100 rounded-md bg-white dark:bg-surface-dark-100 text-text-primary dark:text-text-primary"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="on_hold">On Hold</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          {/* Seniority Filter */}
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-secondary mb-2">
              Seniority Level
            </label>
            <select
              value={filters.seniority}
              onChange={(e) => setFilters(prev => ({ ...prev, seniority: e.target.value }))}
              className="w-full p-2 border border-border-100 dark:border-border-dark-100 rounded-md bg-white dark:bg-surface-dark-100 text-text-primary dark:text-text-primary"
            >
              <option value="">All Levels</option>
              <option value="entry">Entry Level</option>
              <option value="mid-level">Mid Level</option>
              <option value="senior">Senior Level</option>
              <option value="lead">Lead Level</option>
            </select>
          </div>

          {/* Experience Range */}
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-secondary mb-2">
              Experience Range (years)
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                placeholder="Min"
                value={filters.experience_min}
                onChange={(e) => setFilters(prev => ({ ...prev, experience_min: e.target.value }))}
                className="p-2 border border-border-100 dark:border-border-dark-100 rounded-md bg-white dark:bg-surface-dark-100 text-text-primary dark:text-text-primary"
                min="0"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.experience_max}
                onChange={(e) => setFilters(prev => ({ ...prev, experience_max: e.target.value }))}
                className="p-2 border border-border-100 dark:border-border-dark-100 rounded-md bg-white dark:bg-surface-dark-100 text-text-primary dark:text-text-primary"
                min="0"
              />
            </div>
          </div>
        </div>
      </FilterDrawer>
    </div>
  );
}

